<template>
  <property-report-settings
    v-if="!groupsLoading && !propertyLoading"
    :report="report"
    @submit="submit"
    @update-property-details="updatePropertyDetails"
    @cancel="safelyNavigate"
  />
  <q-inner-loading v-else showing color="secondary" />
</template>

<script lang="ts">
import { type AccessUser, defaultColumns, type PropertyReportSubscriptionAttributes } from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import PropertyReportSettings from '@/components/PropertyReports/PropertyReportSettings.vue'
import { computePropertyEdits, deconstructPropertyEdits, propertyDetailToSearch } from '@/utils'
import { useUserStore } from '@/stores/user'
import { usePropertyStore } from '@/stores/property'
import type { RouteLocation } from 'vue-router'
import useGroupStore from '@/stores/group.ts'

export default {
  name: 'PropertyReportNewView',
  components: {
    PropertyReportSettings
  },
  beforeRouteLeave(to, from, next) {
    this.handleRouteChange(next)
  },
  beforeRouteUpdate(to, from, next) {
    this.handleRouteChange(next)
  },
  props: {
    propertyId: {
      type: String as () => string | null,
      default: null
    }
  },
  emits: ['update-property-details'],
  data() {
    return {
      propertyLoading: false,
      groupsLoading: false,
      report: {
        id: -1,
        name: '',
        subject: null,
        comparable_addresses: [],
        excluded_property_ids: [],
        user_id: useUserStore().user!.sub,
        modified_on: new Date().toISOString(),
        created_on: new Date().toISOString(),
        deleted_on: null,
        public_id: null,
        is_organization_visible: false, // TODO: Delete me
        organization_visibility: null,
        tags: ['Active'],
        property_edits: [],
        view_preferences: [],
        is_in_portfolio: null,
        email_schedule: {
          id: -1,
          schedulable_id: -1,
          schedulable_type: 'property_reports',
          created_on: new Date().toISOString(),
          modified_on: new Date().toISOString(),
          deleted_on: null,
          last_execution: null,
          next_execution: null,
          emails: this.currentEmails(),
          cron_expression: '0 13 * * 1' // Default to Mondays at 9AM US Central
        }
      } as PropertyReportSubscriptionAttributes,
      manualPropertyEdits: {} as Record<string, Record<string, string | number>>,
      hasUnsavedChanges: true
    }
  },
  computed: {
    user() {
      return this.$auth0.user.value
    },
    propertyEdits() {
      return computePropertyEdits(this.manualPropertyEdits)
    },
    organization() {
      return useUserStore().organization
    }
  },
  watch: {
    propertyId: {
      immediate: true,
      handler(newPropertyId) {
        this.loadPropertyDetails(newPropertyId)
      }
    }
  },
  mounted() {
    this.setReportColumns()
  },
  methods: {
    handleRouteChange(next: (value?: boolean) => void) {
      if (this.hasUnsavedChanges) {
        void this.showUnsavedChangesDialog().then((shouldLeave) => {
          if (shouldLeave) {
            next()
          } else {
            next(false)
          }
        })
      } else {
        next()
      }
    },
    async loadPropertyDetails(propertyId: string | null) {
      if (propertyId !== null) {
        this.propertyLoading = true
        this.report.subject = null
        this.report.name = ''
        await usePropertyStore()
          .details(propertyId)
          .then((data) => {
            this.report.subject = propertyDetailToSearch(data)
            this.report.name = data.building_name || data.street_address
          })
          .finally(() => {
            this.propertyLoading = false
          })
      } else {
        this.propertyLoading = false
        this.report.subject = null
        this.report.name = ''
      }
    },
    async setReportColumns() {
      this.groupsLoading = true
      const groupId = this.$auth0.user.value?.groupId
      if (!groupId) {
        this.report.columns = defaultColumns
        this.groupsLoading = false
      } else {
        await useGroupStore()
          .show(groupId)
          .then((group) => {
            this.report.columns = group.columns
          })
          .finally(() => {
            this.groupsLoading = false
          })
      }
    },
    currentEmails() {
      const email = this.$auth0.user.value?.email
      return email ? [email] : []
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      if (!this.manualPropertyEdits[propertyId]) {
        this.manualPropertyEdits[propertyId] = {}
      }
      this.manualPropertyEdits[propertyId][key] = value
      this.$emit('update-property-details', deconstructPropertyEdits(this.propertyEdits))
    },
    showUnsavedChangesDialog() {
      if (!this.report.subject && !this.report.comparable_addresses.length) {
        return Promise.resolve(true)
      }
      return new Promise((resolve) => {
        this.$q
          .dialog({
            title: 'Leaving Report Creation',
            message:
              'You are about to leave this page. Any changes you have made will be lost. Are you sure you want to leave?',
            cancel: {
              label: 'Stay',
              flat: true,
              noCaps: true
            },
            ok: {
              label: 'Leave Page',
              flat: true,
              noCaps: true
            },
            persistent: true
          })
          .onOk(() => {
            resolve(true)
          })
          .onCancel(() => {
            resolve(false)
          })
      })
    },
    async submit(report: PropertyReportSubscriptionAttributes, invitePeopleList: AccessUser[]) {
      const reportCreatePayload = { ...report }

      await usePropertyReportStore()
        .create(reportCreatePayload)
        .then(async (createdReport) => {
          this.hasUnsavedChanges = false
          if (createdReport) {
            // Invite people if there are any in the list
            if (invitePeopleList.length > 0) {
              await usePropertyReportStore().createAccess(
                createdReport.id,
                true,
                invitePeopleList.map((_) => ({
                  user_id: _.id,
                  email: _.email,
                  read_only: _.read_only
                }))
              )
            }

            this.$tracker.createdReport(createdReport.id, 'new', 'property')
            this.$router.push({ name: 'Property Report', params: { reportId: createdReport.id } })
          } else {
            this.$router.push({ name: 'Dashboard' })
          }
        })
    },
    safelyNavigate(to: RouteLocation) {
      this.hasUnsavedChanges = false
      this.$router.push(to)
    }
  }
}
</script>
